// src/utils/crewSchedule.js
// r2-cs-engine-a7f3c9e1
//
// The continuous per-crew scheduling engine. PURE — no React, no Firebase.
// Only date-fns (already in both repos). This file is duplicated BYTE-IDENTICAL
// into spatchy-mobile/src/utils/crewSchedule.js, exactly like recurrence.js.
//
// A JOB stays the container (one customer, one invoice). What this models is
// each CREW's SCHEDULE OF APPEARANCES across the life of the job:
//
//   crewSchedule: {
//     [crewGroupId]: {
//       appearances: [
//         { id, date:'YYYY-MM-DD', startAt, endAt, schedulingMode, arrivalWindow },
//         ...many across many weeks
//       ]
//     }
//   }
//
// The appearance is the atom: a dated block for ONE crew, carrying its own
// within-day time. It is what the calendar fans out into an event, what you
// drag, and what Workstream B's timesheet reads start/end straight off of.
//
// DESIGN RULES (from Spec v2):
// - Explicit dated appearances are the STORED TRUTH. Cadence ("Mon/Wed/Fri for
//   3 weeks") is only a GENERATOR that expands to explicit appearances at
//   authoring time. This engine NEVER stores a recurrence rule and NEVER
//   touches recurrence.js. Editing week 3 = editing explicit days.
// - Dates are LOCAL calendar dates ('YYYY-MM-DD'), matching the app's
//   parseLocalDate philosophy (avoid UTC drift on day boundaries).

import { add, startOfDay, format } from 'date-fns';

// day-of-week token <-> JS getDay() index (0=Sun … 6=Sat)
export const DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

// Zero-pad helper for building 'YYYY-MM-DD' from local Y/M/D without UTC.
const pad2 = (n) => String(n).padStart(2, '0');

// A local 'YYYY-MM-DD' string from a JS Date, using LOCAL fields (no toISOString).
export const toDateKey = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Parse a 'YYYY-MM-DD' into a local-midnight JS Date (no UTC shift).
export const fromDateKey = (key) => {
  if (!key || typeof key !== 'string') return null;
  const parts = key.split('-');
  if (parts.length !== 3) return null;
  const [y, m, day] = parts.map((p) => parseInt(p, 10));
  if ([y, m, day].some((n) => Number.isNaN(n))) return null;
  return new Date(y, m - 1, day); // local midnight
};

// Build an appearance id that is stable + unique within a crew: date + a short
// disambiguator (so two appearances on the same day, e.g. AM/PM, don't collide).
// Deterministic given (dateKey, seq) so re-generation is idempotent.
export const makeAppearanceId = (dateKey, seq = 0) => `ap-${dateKey}-${seq}`;

/**
 * expandCadence — turn a days-of-week + date-range cadence into an explicit
 * list of appearance DATE KEYS. This is the "generator", not stored state.
 *
 * @param {Object}   opts
 * @param {string}   opts.startDate  'YYYY-MM-DD' (inclusive)
 * @param {string}   opts.endDate    'YYYY-MM-DD' (inclusive)
 * @param {string[]} opts.daysOfWeek e.g. ['Mon','Wed','Fri'] (tokens from DOW)
 * @returns {string[]} sorted, de-duped 'YYYY-MM-DD' keys for every matching day
 *
 * Boundary behavior: iterates day-by-day from startDate to endDate inclusive,
 * emitting a key whenever that day's weekday is in the selected set. Safe across
 * month/year boundaries because it steps real dates via date-fns `add`.
 */
export const expandCadence = ({ startDate, endDate, daysOfWeek }) => {
  const start = fromDateKey(startDate);
  const end = fromDateKey(endDate);
  if (!start || !end) return [];
  if (end.getTime() < start.getTime()) return [];

  const wanted = new Set(
    (Array.isArray(daysOfWeek) ? daysOfWeek : [])
      .map((t) => DOW[t])
      .filter((n) => n !== undefined)
  );
  if (wanted.size === 0) return [];

  const keys = [];
  let cursor = startOfDay(start);
  const endMs = startOfDay(end).getTime();
  // inclusive of end day
  while (cursor.getTime() <= endMs) {
    if (wanted.has(cursor.getDay())) {
      keys.push(toDateKey(cursor));
    }
    cursor = add(cursor, { days: 1 });
  }
  return keys;
};

/**
 * buildAppearancesFromCadence — expand a cadence AND attach a within-day time
 * template to each generated day, producing ready-to-store appearance objects.
 *
 * @param {Object} opts
 * @param {string}   opts.startDate
 * @param {string}   opts.endDate
 * @param {string[]} opts.daysOfWeek
 * @param {Object}   opts.time   within-day template applied to every day:
 *        { startHour, startMinute, endHour, endMinute, schedulingMode, arrivalWindow }
 *        Times are LOCAL clock times; startAt/endAt are built on each day.
 * @returns {Array} appearance objects (date + Date startAt/endAt + mode + window)
 *
 * NOTE: startAt/endAt are returned as JS Dates here (local). The write layer
 * (JobFormModal save) converts to Firestore Timestamps — the engine stays
 * storage-agnostic, mirroring recurrence.js returning neutral Dates.
 */
export const buildAppearancesFromCadence = ({ startDate, endDate, daysOfWeek, time = {} }) => {
  const {
    startHour = 8, startMinute = 0,
    endHour = 17, endMinute = 0,
    schedulingMode = 'window',
    arrivalWindow = null,
  } = time;

  const keys = expandCadence({ startDate, endDate, daysOfWeek });
  return keys.map((key) => {
    const base = fromDateKey(key); // local midnight
    const startAt = new Date(base.getFullYear(), base.getMonth(), base.getDate(), startHour, startMinute, 0, 0);
    const endAt = new Date(base.getFullYear(), base.getMonth(), base.getDate(), endHour, endMinute, 0, 0);
    return {
      // r2-cs-apptid-unique-2f7b9c14 — id derived from (date, start-time), not
      // batch index. Stacking a second generate on the same day used to restart
      // the index at 0 and collide (ap-<date>-0). The (date, start-time) pair is
      // already the crew's uniqueness invariant — both generate-merge and Add-day
      // dedupe on it — so this id can't collide unless the appearance would have
      // been deduped away anyway. Fully deterministic; more idempotent than before.
      id: makeAppearanceId(key, `${String(startHour).padStart(2, '0')}${String(startMinute).padStart(2, '0')}`),
      date: key,
      startAt,
      endAt,
      schedulingMode,
      arrivalWindow: schedulingMode === 'window' ? arrivalWindow : null,
    };
  });
};

/**
 * flattenAppearances — collect every crew's appearances from a crewSchedule map
 * into a single flat list, tagged with crewGroupId. Order-independent.
 *
 * @param {Object} crewSchedule  { [crewGroupId]: { appearances:[...] } }
 * @returns {Array} [{ crewGroupId, ...appearance }, ...]
 */
export const flattenAppearances = (crewSchedule) => {
  if (!crewSchedule || typeof crewSchedule !== 'object') return [];
  const out = [];
  for (const crewGroupId of Object.keys(crewSchedule)) {
    const entry = crewSchedule[crewGroupId];
    const apps = Array.isArray(entry?.appearances) ? entry.appearances : [];
    for (const a of apps) out.push({ crewGroupId, ...a });
  }
  return out;
};

/**
 * hasCrewSchedule — is this job per-crew scheduled? (any crew with >=1 appearance)
 */
export const hasCrewSchedule = (job) => flattenAppearances(job?.crewSchedule).length > 0;

/**
 * dedupeKey — canonical per-appearance key 'crewGroupId|yyyy-MM-dd-HH-mm'.
 * Mirrors the recurrence engine's yyyy-MM-dd-HH-mm discipline so calendar
 * fan-out can de-dupe appearances against materialized events if ever needed.
 * Accepts a JS Date or Firestore Timestamp for startAt (coerces via toDate()).
 */
export const appearanceDedupeKey = (crewGroupId, startAt) => {
  const d = startAt?.toDate ? startAt.toDate() : (startAt instanceof Date ? startAt : new Date(startAt));
  if (Number.isNaN(d.getTime())) return null;
  return `${crewGroupId}|${format(d, 'yyyy-MM-dd-HH-mm')}`;
};